/**
 * download-authed-csv — 认证 CSV 流下载（admin RBAC 门禁端点的裸 CSV 下载通用逻辑）。
 *
 * 为什么不走 apiRequest：下载端点回的是**裸 CSV 流**（Content-Type text/csv +
 * Content-Disposition attachment），不是 `{ code, data }` envelope → apiRequest 会强解 JSON 失败。
 * 故手写 fetch + JWT 注入，取 blob 触发浏览器保存；非 2xx 时按 envelope 解出业务码抛 AdminApiError，
 * 让调用方走统一错误 toast（不假成功）。`X-Export-Truncated` 透出给调用方提示「超上限仅导部分」。
 *
 * 同款范式的先例是 `use-dashboard.ts` 的 dashboardApi.download（按导出记录 id 取流）；本工具面向
 * 「任意带 query 的导出 URL 取流」，供书籍操作日志 CSV 等新端点复用。dashboardApi.download 暂保留
 * 自有内联副本（未回填到本工具），如后续统一可让它内部改调本函数。
 */

import { AdminApiError, type AdminErrorBody } from '~/api/errors';
import { authedFetch } from '~/api/session-refresh';
import { newRequestId } from '~/lib/request-id';

/** 从 Content-Disposition 解析下载文件名；缺失时回退到调用方给的 fallbackName */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const m = /filename="?([^"]+)"?/.exec(disposition);
  return m?.[1] ?? null;
}

/**
 * 认证下载一个 CSV 流端点并触发浏览器保存。
 * @returns `{ truncated }` —— 服务端 `X-Export-Truncated: true`（超行数上限仅导最新部分）时为 true，
 *   调用方据此提示运营「请缩窄范围」，兑现「不静默截断」。
 */
export async function downloadAuthedCsv(
  url: string,
  fallbackName: string,
): Promise<{ truncated: boolean }> {
  // 鉴权注入 + access 过期(401) 自动 refresh+重试一次 交给 authedFetch；最终仍 401 才在下方 clearSession
  const res = await authedFetch(url, {
    method: 'GET',
    headers: { 'X-Request-Id': newRequestId() },
  });

  if (!res.ok) {
    // 非 2xx：错误响应是 JSON envelope（{ code, message, requestId }）→ 解出业务码抛
    let body: AdminErrorBody | null = null;
    try {
      body = (await res.json()) as AdminErrorBody;
    } catch {
      body = null;
    }
    // 401 登出由 authedFetch 统一处理（仅 refresh 显式失效才 forceLogout）；此处只抛错
    throw new AdminApiError(
      res.status,
      body ?? { code: 'INTERNAL_ERROR', message: `Download failed: ${res.status}`, requestId: '' },
    );
  }

  const truncated = res.headers.get('X-Export-Truncated') === 'true';
  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return { truncated };
}
